import type { Range } from 'components/ui-blocks/click-drag-adjust/ClickDragAdjust';
import config from 'config';
import { EMPTY_ARRAY } from 'constants/defaults';
import { HAS_ADDED_TIME_TO_HOUR } from 'constants/hints/onboarding';
import {
  CLIENT_FOCUS_ON_PROJECTS_BY_DEFAULT,
  DONT_SHOW_BILLABLE_CHANGED_WARNING_AGAIN,
  HAS_INSTALLED_MEMORY_TRACKER,
  PREFERRED_AUTO_NOTE_STRATEGY,
} from 'constants/user';
import { hourAsCardFactory, hourDraftStatusMessagesFactory } from 'factories/hours';
import { makeEmptyEnrichedProject } from 'factories/projects';
import {
  getActiveTagsOnProject,
  getSelectedWithActiveTagsOnProject,
  tagsToDictionary,
} from 'factories/tags';
import { combinedDurationOfMemories, extractNoteFromEntries } from 'factories/timeline';
import { difference, intersection, isEqual, uniq, without } from 'lodash-es';
import { DateTime, Interval } from 'luxon';
import { getUserCapacitiesEntities } from 'redux/modules/capacities';
import { isDayLockedForUser } from 'redux/modules/dayProperties';
import { getDayProperties } from 'redux/modules/dayProperties.selectors';
import { getHourStates } from 'redux/modules/hourStates';
import { getSortedTags, getTagEntities, getTags } from 'redux/modules/tags.selectors';
import { getTicTasks } from 'redux/modules/tic/tasks/tic.tasks.slice';
import { getOpenMemoryConfig } from 'redux/modules/tic/tic.global';
import {
  getAccountCurrency,
  getAccountFeatures,
  getCanUseClientFocusOnProjects,
  getCurrentAccount,
  getHasHourStatesFeature,
} from 'redux/selectors/accounts';
import { getFilter } from 'redux/selectors/calendar.filter';
import { getClients } from 'redux/selectors/clients';
import { getHints } from 'redux/selectors/hints';
import {
  getHourEntities,
  getHourIsSaving,
  getHourProjectEntities,
  getHoursAsCards,
  getHourXhrError,
  getShowHourHistoryModal,
} from 'redux/selectors/hours';
import {
  getHourDraftDay,
  getHourDraftEntryIds,
  getHourDraftExternalLinks,
  getHourDraftHasEntries,
  getHourDraftId,
  getHourDraft as getHourDraftImported,
  getHourDraftProjectId,
  getHourDraftShowAllProjectsForDraft,
  getHourDraftSuggestionId,
  getHourDraftTagIds,
  getHourDraftTimestamps,
  getHourDraftUserId,
  getLoggedWeeklyTotal,
  readAutoNoteStrategy,
  readHourDraftDay,
  readHourDraftId,
} from 'redux/selectors/hours.draft.base';
import { resolveClientFocusOnProjects } from 'redux/selectors/hours.draft.client-focus-on-projects';
import {
  getProjectDraftStatus,
  getProjectPermissionsMap,
  getProjectVariantMap,
  getProjectXhrStatus,
} from 'redux/selectors/projects';
import { getSuggestions } from 'redux/selectors/suggestions';
import {
  getMemoryEntries,
  getMemoryViewmode,
  getTimelineEntities,
  getTimelineEntries,
  getTimestampSnappingMinutes,
} from 'redux/selectors/timeline';
import {
  getCanUseTimesheetAssistantV2,
  getHasEnabledTic,
  getIsUserAdmin,
  getIsUserInExperimentalFeatureGroup,
  getUser,
  getUserId,
  getUserPermissions,
  getUserProperties,
  getUserRoleId,
} from 'redux/selectors/user';
import { getDeletedUsers, getNonDeletedUsers } from 'redux/selectors/users';
import { getHistoricCapacityByDate } from 'redux/selectors/utils/capacities';
import type { IState } from 'redux/state';
import { createSelector } from 'reselect';
import { getForecastEntities } from 'routes/forecast/redux/modules/forecast.selectors';
import type {
  AutoNoteStrategy,
  EnrichedHourDraft,
  HourAsCard,
  HourDraft,
  HourEditorSuggestedDurations,
} from 'types/hour';
import { HourLockedReasons } from 'types/hour';
import type { EnrichedProject, Project } from 'types/project';
import { TagSettings } from 'types/project';
import type { Tag } from 'types/tag';
import type { Entry } from 'types/timeline';
import type { Duration } from 'types/units';
import type { User } from 'types/user';
import { prefixHexColor } from 'utils/colors';
import { isAfter } from 'utils/date-helper';
import { calculateDuration } from 'utils/durations';
import { parseAutoNoteStrategy } from 'utils/hours';
import { isDurationEqualToSumOfMemories } from 'utils/memories';
import { naturalCompare } from 'utils/naturalSort';
import { roundUpToFactorOf } from 'utils/numbers';
import { getProjectColor } from 'utils/project';
import { lazySelector } from 'utils/reselect';
import { memorySidebarIsVisible } from 'utils/timeline';
import { notNull } from 'utils/typesafe';
import { getIsViewingTimesheet } from '../../domains/ai/timesheet_proposals/redux/tic.timesheetProposals.slice';
import { getPinnedProjectIdsSet } from './pins';
import { getIsRouteCalendar, getIsRouteReports } from './routing';
import { getSubscriptionModalMessageKey } from './subscriptions';
import { getUserLedUserIds } from './teams';
import { enrichProjectVariantOrNull, extractProjectBase } from './utils/projects';

export const getHourDraft = getHourDraftImported;

// Circular dependencies makes it impossible to import from calendar selector?
// Halp?!
const readCalendarSelectionHourIds = (state: IState) => state.calendar.selection.hourIds;

export const getGeneratedNotes = (state: IState) => state.hours.generatedNotes;

export const getShowCopyHourModal = createSelector(
  [readCalendarSelectionHourIds],
  (hourIds): boolean => !!hourIds.length
);

const getHasMemoriesWithinHourDraftTimespan = lazySelector(
  import('./timeline.memories').then(m => m.getHasMemoriesWithinHourDraftTimespan)
);

/**
 * Disable clickOutside/focus trap on HourEditor when we have secondary modals
 * overlaying the HourEditor
 */
export const getHourEditorDisableTrap = createSelector(
  [
    getShowCopyHourModal,
    getShowHourHistoryModal,
    getProjectDraftStatus,
    getSubscriptionModalMessageKey,
    getOpenMemoryConfig,
  ],
  (
    showCopyModal,
    showHourHistoryModal,
    projectDraftStatus,
    subscriptionMessageKey,
    openMemoryConfig
  ): boolean =>
    showCopyModal ||
    showHourHistoryModal ||
    projectDraftStatus.exists ||
    !!subscriptionMessageKey ||
    !!openMemoryConfig
);

/**
 * Get the date based on hour.draft.day, or fall back to calendar's date
 */
export const getHourDraftOrCalendarDate = createSelector(
  [readHourDraftDay, getFilter],
  // If we don't have a draft, fall back to calendar day (used in Day View)
  (draftDay, filter): string => draftDay || filter.date
);

/**
 * Return the entry_ids on the Hour Suggestions for current day
 * Excludes the suggestion currently being reviewed.
 */
export const getEntryIdsAssociatedToSuggestionsOnDraftDay = createSelector(
  [getHourDraftSuggestionId, getHourDraftOrCalendarDate, getSuggestions],
  (draftSuggestionId, day, allSuggestions): Array<number> => {
    let entryIds: Array<number> = [];
    allSuggestions.forEach(suggestion => {
      if (suggestion.date === day && suggestion.id !== draftSuggestionId) {
        entryIds = [...entryIds, ...suggestion.suggested_entry_ids];
      }
    });
    entryIds = uniq(entryIds);
    return entryIds.length ? entryIds : EMPTY_ARRAY;
  }
);
/**
 * Returns entry_ids for the Hour's on current day
 */
export const getEntryIdsAssociatedToHoursOnDraftDay = createSelector(
  [readHourDraftId, getHourDraftOrCalendarDate, getHoursAsCards],
  (draftId, day, allHourCards): Array<number> => {
    let entryIds: Array<number> = [];
    allHourCards.forEach(hour => {
      if (hour.id !== draftId && hour.day === day) {
        entryIds = [...entryIds, ...hour.entryIds];
      }
    });
    entryIds = uniq(entryIds);
    return entryIds.length ? entryIds : EMPTY_ARRAY;
  }
);

/**
 * Is the project selected on an hour editable in the current session?
 *
 * Admins bypass every check: as part of TT-4409 they are allowed to move
 * entries to and from archived projects, so neither the archived nor the
 * membership gate must lock the entry for them.
 *
 * For non-admins the project must be active, and EITHER:
 *   - the current user is the entry owner (always allowed on their own
 *     hours on active projects — per-entry guards still apply); or
 *   - both the entry owner and the current user (the editor) are
 *     members of the project (manager / team lead path).
 *
 * The own-entry branch matches the backend's authorize_hour_edit! rule
 * in hours_controller_concern: edit access to one's own historical
 * entries is decoupled from current project membership.
 */
export const getHourDraftProjectIsAvailable = createSelector(
  [
    getHourDraftProjectId,
    getProjectVariantMap,
    getHourDraftUserId,
    getUserId,
    getProjectPermissionsMap,
    getIsUserAdmin,
  ],
  (projectId, projectVariantMap, ownerUserId, currentUserId, permissionsMap, isAdmin): boolean => {
    if (!projectId) {
      return false;
    }

    // Project must exist in local state.
    if (!projectVariantMap[projectId]) {
      return false;
    }

    // Admins can edit entries on any project, including archived ones
    // (TT-4409: admins may move entries to/from archived projects).
    if (isAdmin) {
      return true;
    }

    // Non-admins: the project must be active.
    const isActive = extractProjectBase(projectVariantMap[projectId]!).active;
    if (!isActive) {
      return false;
    }

    // Own entry on an active project is always editable.
    if (ownerUserId != null && currentUserId === ownerUserId) {
      return true;
    }

    const isOwnerMember = !!permissionsMap[ownerUserId!]?.includes(projectId);
    const isCurrentUserMember = !!permissionsMap[currentUserId!]?.includes(projectId);

    return isOwnerMember && isCurrentUserMember;
  }
);

export const getHourDraftSource = createSelector([getHourDraft], draft => {
  return draft?.source;
});

export interface GetHourDraftStatus {
  exists: boolean;
  isEditingExisting: boolean;
  isCreatingNew: boolean;
  isValid: boolean;
  forDay: string | null | undefined;
  forUserId: number | null;
  isSuggestionInReview: boolean;
  isLocked: boolean;
  hasEntries: boolean;
  isProjectAvailable: boolean;
  isFromPendingDraft: boolean;
}

export const getHasHourDraft = createSelector([readHourDraftDay], day => !!day);

export const getHourDraftProject = createSelector(
  [getHourDraftProjectId, getHourProjectEntities, getProjectVariantMap, getPinnedProjectIdsSet],
  (id, projectEntities, globalProjectVariantMap, pinnedProjectIdsSet): EnrichedProject | null => {
    const projectId = id || 0;
    const hourEmbeddedProject =
      projectEntities &&
      projectEntities[projectId] &&
      ({ type: 'light', lightProject: projectEntities[projectId] } as const);
    const variant = globalProjectVariantMap[projectId];
    const project = variant?.type === 'full' ? variant : (hourEmbeddedProject ?? variant);
    return enrichProjectVariantOrNull(project, pinnedProjectIdsSet) ?? null;
  }
);

interface ProjectSubsetType {
  id: number | null | undefined;
  name: string | null | undefined;
  color: string | null | undefined;
}
export const getHourDraftProjectMeta = createSelector(
  [getHourDraftProject],
  (project): ProjectSubsetType => ({
    id: project ? project.base.id : null,
    name: project ? project.base.name : null,
    color: project
      ? prefixHexColor(getProjectColor(project.base))
      : `#${config.suggestionDefaultProjectColor}`,
  })
);

const getDraftOwner = createSelector(
  [getHourDraftUserId, getNonDeletedUsers, getDeletedUsers, getUser],
  (draftOwnerId, users, deletedUsers, authenticatedUser): User => {
    const userInGlobalState = [...users, ...deletedUsers].find(({ id }) => id === draftOwnerId);
    // Fall back to the authenticated user (we might want to only do this if it matches userId?)
    // (this is should only happen in users.json isn't loaded yet)
    return userInGlobalState || authenticatedUser;
  }
);

const getSelectedMemoryEntries = createSelector(
  [getTimelineEntities, getHourDraftEntryIds],
  (entries, selected): Array<Entry> => selected.map(id => entries[id] || null).filter(notNull)
);

export const getSelectedMemoryEntriesChronologically = createSelector(
  [getSelectedMemoryEntries],
  (entries): Array<Entry> => [...entries].sort((a, b) => naturalCompare(a.from, b.from))
);

export const getPreferredAutoNoteStrategy = createSelector(
  [getUserProperties],
  (userProperties): AutoNoteStrategy => {
    // New users do not get this property, so this is where we set the default to 'off'
    return parseAutoNoteStrategy(userProperties[PREFERRED_AUTO_NOTE_STRATEGY]) || 'off';
  }
);

const getTicAndLegacyMemories = lazySelector(
  import('redux/modules/tic/tic.memories.selectors').then(m => m.getTicAndLegacyMemories)
);
const getNoteFromSelectedMemoryEntries = createSelector(
  [getTicAndLegacyMemories, getTimelineEntries, getHourDraft, readAutoNoteStrategy],
  (memories, entries, hourDraft, strategy): string => {
    return extractNoteFromEntries(memories ?? [], entries, hourDraft, strategy);
  }
);

const getNoteForHourDraft = createSelector(
  [readAutoNoteStrategy, getNoteFromSelectedMemoryEntries, getGeneratedNotes],
  (strategy, noteFromMemories, generatedNotes): string => {
    switch (strategy) {
      case 'memory-titles':
        return noteFromMemories;
      case 'with_reference':
      case 'without_reference':
        if (generatedNotes) {
          return generatedNotes[strategy];
        }
        return '...';
      case 'off':
        return '';
      default:
        return '';
    }
  }
);

export const getHourNoteGenerationState = createSelector(
  [getHourDraft, getGeneratedNotes],
  (draft, generatedNotes) => {
    const isNoteGenerated = !!generatedNotes;
    return {
      generatedNotes,
      shouldRegenerateNote: !!draft?._shouldRegenerateNote || !isNoteGenerated,
      isGeneratingNote: !!draft?._isGeneratingNote,
      isNoteGenerated,
    };
  }
);

export const getEnrichedHourDraft = createSelector(
  [getHourDraft, getHourDraftProject, getNoteForHourDraft],
  (draft, actualProject, note): EnrichedHourDraft | undefined => {
    if (!draft) {
      return undefined;
    }
    // If we don't have a project, flesh out the basic structure
    const project: EnrichedProject = actualProject ?? makeEmptyEnrichedProject();

    return {
      ...draft,
      _suggestedNote: note,
      project,
    };
  }
);

const getUserCanEditDraft = createSelector(
  [getEnrichedHourDraft, getHasHourStatesFeature, getUserRoleId, getUserLedUserIds, getIsUserAdmin],
  (draft, hasHourStatesFeature, userRoleId, userLedUserIds, isAdmin) => {
    // Admins can edit entries on archived projects (TT-4409 move-to-archived);
    // non-admins keep the existing lock.
    if (draft?.project.base.active === false && !isAdmin) return false;

    // New drafts can always be edited
    if (!draft?.id) return true;

    let canEdit = draft.manage;
    if (hasHourStatesFeature) {
      const userIsTeamLead = userLedUserIds.has(draft.user.id);
      canEdit &&=
        draft.state === null ||
        draft.state.permissions.some(
          p => p.role_id === userRoleId || (p.team_lead && userIsTeamLead)
        );
    }
    return canEdit;
  }
);

/**
 * Draft is locked if hour is billed, locked or draft owner doesn't have access to the project.
 */
export const getHourDraftIsLocked = createSelector(
  [getHourDraft, getHourDraftProjectIsAvailable, getHasHourStatesFeature, getUserCanEditDraft],
  (draft, isProjectAvailable, hasHourStatesFeature, canUserMakeChanges): boolean => {
    // New drafts are never locked
    if (!draft?.id) return false;

    // Always lock draft if user can't edit
    if (!canUserMakeChanges) return true;

    const isUnlocked = draft._unlockedByUser;
    const lockDueToDraftProperties = hasHourStatesFeature
      ? draft.state?.locked || !canUserMakeChanges
      : draft.locked;
    const lockDueToUnavailableProject = !!draft.id && !isProjectAvailable;
    const shouldBeLocked = lockDueToDraftProperties || lockDueToUnavailableProject;

    return shouldBeLocked && !isUnlocked;
  }
);

// TODO: We'll eventually need to include draft duration/cost here too
// since we want sums/durations to be accurate
export const getHourDraftStatus = createSelector(
  [
    getHourDraftId,
    readHourDraftDay,
    getHourDraftUserId,
    getHourDraftSuggestionId,
    getHourDraftProjectId,
    getHourDraftIsLocked,
    getHourDraftHasEntries,
    getHourDraftProjectIsAvailable,
    getHourDraftSource,
  ],
  (
    id,
    day,
    forUserId,
    suggestionId,
    projectId,
    isLocked,
    hasEntries,
    isProjectAvailable,
    source
  ) => {
    const isEditingExisting = !!(id && day);
    const isCreatingNew = !!(!id && day); // drafts don't have id yet
    const isValid = !!day && !!projectId; // any draft with day considered valid
    const forDay = day || null;
    const isSuggestionInReview = !!suggestionId && !id;

    return {
      exists: getHasHourDraft.resultFunc(day),
      isValid,
      forDay,
      isEditingExisting,
      isCreatingNew,
      isSuggestionInReview,
      forUserId,
      isLocked,
      hasEntries,
      // Intentionally left true if no projectId exists, to deal with initial state of HourEditor when creating new
      isProjectAvailable: projectId ? isProjectAvailable : true,
      isFromPendingDraft: source === 'pending',
    };
  }
);

export const getDurationOfDraftTimestamps = createSelector([getHourDraftTimestamps], timestamps => {
  const totalSeconds = timestamps
    .map(({ from, to }) => Interval.fromISO(`${from}/${to}`).toDuration().as('seconds'))
    .reduce((total, current) => total + current, 0);

  return calculateDuration(totalSeconds);
});

export const getDurationOfSelectedMemories = createSelector([getSelectedMemoryEntries], entries =>
  combinedDurationOfMemories(entries)
);

const canAutoUpdateDraftDurationBasedOnTimestamps = createSelector(
  [getHourDraft, getDurationOfDraftTimestamps],
  (draft, durationOfTimestamps) => {
    if (!draft) {
      return false;
    }

    return (
      draft.duration.total_seconds === 0 ||
      draft.duration.total_seconds === durationOfTimestamps.total_seconds
    );
  }
);

const canAutoUpdateDraftDurationBasedOnMemories = createSelector(
  [getHourDraft, getMemoryEntries, getHourDraftEntryIds],
  (draft, entries, memoryIds) => {
    if (!draft) {
      return false;
    }
    const { duration } = draft;
    return isDurationEqualToSumOfMemories({ entries, memoryIds, duration });
  }
);

export const canAutoUpdateDraftDuration = createSelector(
  [
    canAutoUpdateDraftDurationBasedOnTimestamps,
    canAutoUpdateDraftDurationBasedOnMemories,
    getHasEnabledTic,
  ],
  (basedOnTimestamps, basedOnMemories, hasExperimentalBehaviour) =>
    hasExperimentalBehaviour ? basedOnTimestamps : basedOnMemories
);

export const getDraftDurationMode = createSelector(
  [getHourDraft, canAutoUpdateDraftDuration],
  (draft, canAutoUpdateDuration) => {
    const draftHasNonZeroDuration = (draft?.duration.total_hours ?? 0) > 0;
    const autoDurationMode = canAutoUpdateDuration && draftHasNonZeroDuration;
    return autoDurationMode ? 'memory' : 'manual';
  }
);

export const getHourDraftAsCard = createSelector(
  [
    getEnrichedHourDraft,
    getDurationOfSelectedMemories,
    getDraftDurationMode,
    getTagEntities,
    getHasEnabledTic,
    getAccountCurrency,
    getHasHourStatesFeature,
    getUserRoleId,
    getHourStates,
    getUserLedUserIds,
  ],
  (
    draft,
    durationOfMemories,
    durationMode,
    tags,
    hasDurationFromTimestamps,
    defaultCurrency,
    hasHourStatesFeature,
    userRoleId,
    states,
    userLedUserIds
  ): HourAsCard | null => {
    if (!draft) {
      return null;
    }

    const duration = durationMode === 'memory' ? durationOfMemories : draft.duration;

    return hourAsCardFactory(draft, {
      isEditing: true, // Draft exists = we're editing it, right?
      duration,
      tags,
      hasDurationFromTimestamps,
      defaultCurrency,
      hasHourStatesFeature,
      userRoleId,
      states,
      userLedUserIds,
    });
  }
);

export const getHourEditorMemoryPreferences = createSelector(
  [getUserProperties, getUser, getDraftOwner, getFilter, getHasEnabledTic],

  (properties, user, draftOwner, filter, hasEnabledTic) => {
    const isVisible = memorySidebarIsVisible({
      userId: user.id,
      draftUserId: draftOwner.id,
      multiUserMode: filter.multiUserMode,
      properties,
      hasEnabledTic,
    });
    return { isVisible };
  }
);

export const getIsActingAsOtherUser = createSelector(
  [getUserId, getHourDraftUserId],
  (userId, hourDraftUserId): boolean => {
    return userId !== hourDraftUserId;
  }
);

export interface GetHourWithHourEditorModalProps {
  disableTrap: boolean;
  draft: {
    id: number | null | undefined;
    day: string | false;
  };
  ui: {
    showMemory: boolean;
    /** Opened from AutoSheet, which has its own timeline: the editor shows alone and holds the page still. */
    isAutosheet: boolean;
    isActingAsOtherUser: boolean;
    hasSelectedProject: boolean;
    hasLinkedMemories: boolean;
    showTags: boolean;
    hasEnabledTic: boolean;
  };
}

const getCanAccessAllTags = createSelector(
  [getHourDraftProject],
  hourDraftProject => hourDraftProject?.fullProject?.enable_labels === TagSettings.All
);

export const getProjectTagIds = createSelector(
  [getHourDraftProject, getCanAccessAllTags],
  (hourDraftProject, canAccessAllTags) => {
    if (canAccessAllTags || !hourDraftProject?.fullProject) {
      // Reduce selector load - don't pass around the label ids when they are not needed
      return EMPTY_ARRAY;
    }

    const tagIds = without(
      hourDraftProject.fullProject.label_ids ?? [],
      ...(hourDraftProject.fullProject.default_label_ids ?? [])
    );

    return tagIds.length ? tagIds : EMPTY_ARRAY;
  },
  { memoizeOptions: { resultEqualityCheck: isEqual } }
);

const getSavedEntry = createSelector([getHourEntities, getHourDraft], (hourEntities, hourDraft) =>
  hourEntities && hourDraft?.id ? hourEntities[hourDraft.id] : null
);

export const getAllowedTagsForHourDraft = createSelector(
  [getSortedTags, getProjectTagIds, getCanAccessAllTags, getSavedEntry],
  (tags, projectTagIds, canAccessAllTags, savedEntry) => {
    // If project tags aren't specified, allow all tags by default:
    if (canAccessAllTags && !projectTagIds.length) {
      const allTagIds = tags.reduce((ids: Array<number>, t) => {
        ids.push(t.id);
        if (t.children) {
          ids.push(...t.children.map(c => c.id));
        }
        return ids;
      }, []);
      return tags
        .map(tag => {
          if (savedEntry?.id && savedEntry?.label_ids.length > 0) {
            return getSelectedWithActiveTagsOnProject(tag, savedEntry, allTagIds);
          }

          return getActiveTagsOnProject(tag, allTagIds);
        })
        .filter(notNull);
    }

    if (!canAccessAllTags && !projectTagIds.length && !savedEntry?.label_ids?.length) {
      return [];
    }
    // Otherwise, return only the tags allowed on this project:
    return tags
      .map(tag => {
        if (savedEntry?.id && savedEntry?.label_ids.length > 0) {
          return getSelectedWithActiveTagsOnProject(tag, savedEntry, projectTagIds);
        }

        return getActiveTagsOnProject(tag, projectTagIds);
      })
      .filter(notNull);
  }
);

export const getAllowedTagsMap = createSelector([getAllowedTagsForHourDraft], tags =>
  tagsToDictionary(tags)
);

/** AutoSheet in place of the classic calendar, on the calendar route. */
export const getIsShowingAutosheet = createSelector(
  [getIsViewingTimesheet, getCanUseTimesheetAssistantV2],
  (isViewingTimesheet, canUseTimesheetAssistantV2) =>
    isViewingTimesheet && canUseTimesheetAssistantV2
);

export const getHourWithHourEditorModalProps = createSelector(
  [
    getHourDraftId,
    getHourDraftDay,
    getHourDraftProjectId,
    getHourEditorMemoryPreferences,
    getHourEditorDisableTrap,
    getIsActingAsOtherUser,
    getIsRouteCalendar,
    getHourDraftEntryIds,
    getAllowedTagsForHourDraft,
    getHourDraftTagIds,
    getHasEnabledTic,
    getIsShowingAutosheet,
  ],
  (
    id,
    day,
    projectId,
    memoryPreferences,
    disableTrap,
    isActingAsOtherUser,
    isRouteCalendar,
    entryIds,
    allowedTags,
    selectedTags,
    hasEnabledTic,
    isShowingAutosheet
  ): GetHourWithHourEditorModalProps => {
    return {
      disableTrap,
      draft: {
        day,
        id,
      },
      ui: {
        isActingAsOtherUser,
        isAutosheet: isRouteCalendar && isShowingAutosheet,
        showMemory: memoryPreferences.isVisible && isRouteCalendar && !isShowingAutosheet,
        hasSelectedProject: !!projectId,
        hasLinkedMemories: !!entryIds.length,
        showTags: allowedTags.length > 0 || selectedTags.length > 0,
        hasEnabledTic,
      },
    };
  }
);

export const getRequiredTagIdsForHourDraft = createSelector(
  [getHourDraftProject],
  hourDraftProject => {
    return hourDraftProject?.fullProject?.required_label_ids ?? EMPTY_ARRAY;
  },
  { memoizeOptions: { resultEqualityCheck: isEqual } }
);
export const getRequiredTagsForHourDraft = createSelector(
  [getRequiredTagIdsForHourDraft, getTags],
  (required, tags): Array<Tag> => {
    return tags.filter(tag => required.includes(tag.id));
  }
);

export const isHourDraftMissingRequiredTags = createSelector(
  [getEnrichedHourDraft, getRequiredTagIdsForHourDraft],
  (draft, requiredTags: Array<number> = []): boolean => {
    if (!requiredTags.length) {
      return false;
    }

    // Find the ids of required tags in use:
    const requiredTagsUsed = intersection(draft?.label_ids ?? [], requiredTags);

    // Do the number of required tags match the number of selected required tags?
    return requiredTagsUsed.length !== requiredTags.length;
  }
);

export const isHourDraftExceedingSingleTagLimit = createSelector(
  [getEnrichedHourDraft, getHourDraftProject, getAllowedTagsMap],
  (draft, hourDraftProject, tagsMap): boolean => {
    const project = hourDraftProject?.fullProject;
    if (!project || !project.allow_only_one_tag) {
      return false;
    }
    const labelIdsExcludingDefault = difference(
      draft?.label_ids ?? [],
      project.default_label_ids ?? []
    );
    const labelsFeatureEnabled =
      (project.enable_labels === 'custom' && (project.label_ids?.length ?? 0) > 0) ||
      project.enable_labels === 'all';
    if (!labelsFeatureEnabled) {
      return false;
    }

    const selected = new Set<number>(labelIdsExcludingDefault);
    for (const id of Array.from(selected)) {
      const children = tagsMap[id]?.children ?? [];
      if (children.some(child => selected.has(child.id))) {
        selected.delete(id);
      }
    }

    return selected.size > 1;
  }
);

export const isHourDraftMissingSingleTag = createSelector(
  [getEnrichedHourDraft, getHourDraftProject],
  (draft, hourDraftProject): boolean => {
    const project = hourDraftProject?.fullProject;
    if (!project || !project.allow_only_one_tag) {
      return false;
    }
    const labelIdsExcludingDefault = difference(
      draft?.label_ids ?? [],
      project.default_label_ids ?? []
    );
    const labelsFeatureEnabled =
      (project.enable_labels === 'custom' && (project.label_ids?.length ?? 0) > 0) ||
      project.enable_labels === 'all';
    if (!labelsFeatureEnabled) {
      return false;
    }
    return labelIdsExcludingDefault.length === 0;
  }
);

export const isMissingRequiredAnyTag = (
  hourDraft: HourDraft | undefined,
  project: Project | null | undefined
) => {
  if (hourDraft == null || project == null) return false;
  const labelIdsExcludingDefault = difference(hourDraft.label_ids, project.default_label_ids ?? []);
  return (
    project.required_labels &&
    labelIdsExcludingDefault.length === 0 &&
    ((project.enable_labels === 'custom' && (project.label_ids?.length ?? 0) > 0) ||
      project.enable_labels === 'all')
  );
};

const getIsHourDraftMissingRequiredAnyTag = createSelector(
  [getHourDraft, getHourDraftProject],
  (draft, project): boolean => isMissingRequiredAnyTag(draft, project?.fullProject)
);

/**
 * Returns (up to) 4 suggested bumps to the current duration on Hour.
 * 4th suggestion returned only if we have time on related Memories.
 */
const emptyDraftDuration = {
  durationForMode: calculateDuration(0),
  suggestedDurations: EMPTY_ARRAY,
};

type GetHourEditorDurationSuggestions = {
  durationForMode: Duration;
  suggestedDurations: HourEditorSuggestedDurations;
};

const getDurationForSuggestions = createSelector(
  [getDurationOfDraftTimestamps, getDurationOfSelectedMemories, getHasEnabledTic],
  (durationOfTimestamps, durationOfMemories, hasExperimentalBehaviour) =>
    hasExperimentalBehaviour ? durationOfTimestamps : durationOfMemories
);

export const getHourEditorDurationSuggestions = createSelector(
  [getDurationForSuggestions, getHourDraft, getDraftDurationMode],
  (durationOfMemories, draft, draftDurationMode): GetHourEditorDurationSuggestions => {
    // If we have no draft, return no suggestions
    if (!draft) {
      return emptyDraftDuration;
    }

    const isLiveUpdatedFromMemories =
      draftDurationMode === 'memory' && draft.timer_state !== 'start';

    const baseSeconds = isLiveUpdatedFromMemories
      ? durationOfMemories.total_seconds
      : draft.duration.total_seconds;

    const secondsStep1 = roundUpToFactorOf(baseSeconds, 15 * 60, { mustIncrease: true });
    const secondsStep2 = roundUpToFactorOf(secondsStep1, 15 * 60, { mustIncrease: true });
    const secondsStep3 = roundUpToFactorOf(secondsStep2, 60 * 60, { mustIncrease: true });

    const suggestedDurations: HourEditorSuggestedDurations = [
      {
        key: 1,
        seconds: secondsStep1,
        // diffSeconds: secondsStep1 - baseSeconds,
        isActive: !isLiveUpdatedFromMemories && draft.duration.total_seconds === secondsStep1,
      },
      {
        key: 2,
        seconds: secondsStep2,
        // diffSeconds: secondsStep2 - baseSeconds,
        isActive: !isLiveUpdatedFromMemories && draft.duration.total_seconds === secondsStep2,
      },
      {
        key: 3,
        seconds: secondsStep3,
        // diffSeconds: secondsStep3 - baseSeconds,
        isActive: !isLiveUpdatedFromMemories && draft.duration.total_seconds === secondsStep3,
      },
    ];

    // If we have suggested time on memory selection
    if (durationOfMemories.total_seconds > 0) {
      suggestedDurations.fill(
        {
          key: 3,
          seconds: durationOfMemories.total_seconds,
          isActive: isLiveUpdatedFromMemories,
          isBasedOnMemories: true,
        },
        -1
      );
    }

    return {
      durationForMode: isLiveUpdatedFromMemories ? durationOfMemories : draft.duration,
      suggestedDurations,
    };
  }
);

export const getHourEditorHasUnresolvedXhr = createSelector(
  [getHourIsSaving, getProjectXhrStatus],
  (hourIsSaving, projectXhrStatus): boolean => {
    return hourIsSaving || projectXhrStatus.isLoading;
  }
);

type Hinted = null | {
  TimeSection: {
    showHint: boolean;
  };
};
export const getHintsForHourEditor = createSelector(
  [getHints, getEnrichedHourDraft, getHourDraftStatus],
  (hints, draft, draftStatus): Hinted => {
    const creatingNew = draftStatus.isCreatingNew && !draftStatus.isSuggestionInReview;
    const hasProject = !!draft?.project.base.id;
    const hasNote = (draft?.note ?? '') !== '';
    const showTimeHint = hasProject && hasNote && creatingNew;

    return {
      TimeSection: {
        showHint: showTimeHint && hints[HAS_ADDED_TIME_TO_HOUR],
      },
    };
  }
);

export const getShouldFetchProjectDetails = createSelector(
  [getHourDraftProjectId],
  hourDraftProjectId => {
    return !!hourDraftProjectId;
  }
);

export const getIsDraftDayLockedForUser = createSelector(
  [getHourDraft, getDayProperties],
  (draft, dayProperties) => {
    if (!draft) {
      return false;
    }
    return isDayLockedForUser(draft.user.id, dayProperties[draft.day]);
  }
);

const getHourEditorMessages = createSelector(
  [
    getUser,
    getHourXhrError,
    getEnrichedHourDraft,
    getRequiredTagsForHourDraft,
    getDraftOwner,
    getHourDraftStatus,
    isHourDraftMissingRequiredTags,
    getIsHourDraftMissingRequiredAnyTag,
    isHourDraftExceedingSingleTagLimit,
    getHourDraftIsLocked,
    getIsDraftDayLockedForUser,
    getHourEditorHasUnresolvedXhr,
    getHasHourStatesFeature,
    getUserCanEditDraft,
    getUserPermissions,
  ],
  (
    user,
    xhrError,
    draft,
    requiredTags,
    draftOwner,
    hourDraftStatus,
    isMissingRequiredTags,
    isMissingRequiredAnyTag,
    exceedingSingleTagLimit,
    isLocked,
    isDayLocked,
    isXhrRunning,
    hasHourStatesFeature,
    canEdit,
    { canUnlockDays }
  ) => {
    const isActingAsOtherUser = !!draft?.user && user.id !== draft?.user.id;
    const { isProjectAvailable } = hourDraftStatus;
    const isProjectAvailableAfterXhrs = (isActingAsOtherUser && isXhrRunning) || isProjectAvailable;
    const isMissingRequiredNote =
      draft?.project?.fullProject?.required_notes === true &&
      (!draft?.note || draft?.note?.length < 1);
    const isBasedOnSuggestion = !!draft?.suggestion_id && draft.id == null;
    const isBilled = !!(draft?.locked && draft?.locked_reason === HourLockedReasons.Billed);
    const isInvoiced = !!(draft?.locked && draft?.locked_reason === HourLockedReasons.Invoiced);
    const isMaybeChangedDueToPusherUpdate = !!draft?._pusherUpdatedAt;
    return hourDraftStatusMessagesFactory({
      xhrError, // from selector
      draft: draft!, // from selector
      draftOwner, // from selector
      isMissingRequiredTags,
      isMissingRequiredAnyTag,
      isActingAsOtherUser, // needs draft and user * probable selector
      isBasedOnSuggestion, // available from draft
      isBilled, // available from draft
      canEdit,
      isLocked,
      isDayLocked,
      isExceedingSingleTagLimit: exceedingSingleTagLimit,
      canUnlockDays,
      requiredTags,
      isProjectAvailable: isProjectAvailableAfterXhrs,
      isMissingRequiredNote,
      isLoading: isXhrRunning,
      isInvoiced,
      isMaybeChangedDueToPusherUpdate,
      hasHourStatesFeature,
      currentUser: user,
    });
  }
);

const getDraftMeta = createSelector(
  [
    getCurrentAccount,
    getEnrichedHourDraft,
    getUser,
    isHourDraftMissingRequiredTags,
    getIsHourDraftMissingRequiredAnyTag,
    isHourDraftExceedingSingleTagLimit,
    getHourEditorHasUnresolvedXhr,
    getHourDraftIsLocked,
    getIsDraftDayLockedForUser,
    getIsRouteReports,
    getHourDraftStatus,
    getDeletedUsers,
    getIsRouteCalendar,
    getForecastEntities,
    getMemoryViewmode,
  ],
  (
    account,
    draft,
    user,
    isMissingRequiredTags,
    isMissingRequiredAnyTag,
    exceedingSingleTagLimit,
    isLoading,
    isLocked,
    isDayLocked,
    isOnReportsRoute,
    hourDraftStatus,
    deletedUsers,
    isRouteCalendar,
    forecasts,
    memoryViewMode
  ) => {
    const isActingAsOtherUser = !!draft?.user && user.id !== draft?.user.id;
    const isBasedOnSuggestion = !!draft?.suggestion_id && draft?.id == null;
    const hasPlannedTime =
      !!draft?.estimated_duration && draft.estimated_duration.total_seconds > 0;
    const isBilled = !!(draft?.locked && draft?.locked_reason === HourLockedReasons.Billed);
    const isInvoiced = !!(draft?.locked && draft?.locked_reason === HourLockedReasons.Invoiced);
    const canManage = !!draft?.manage;
    const hasValidDraft: boolean = !!(draft?.day && draft?.project?.base?.id);
    const isMissingRequiredNote =
      !!draft?.project?.fullProject?.required_notes === true && (draft?.note?.length ?? 0) < 1;
    const canSetTimespan = draft?.timer_state !== 'start';
    const isSaveDisabled =
      !hasValidDraft ||
      isMissingRequiredNote ||
      isMissingRequiredTags ||
      isMissingRequiredAnyTag ||
      exceedingSingleTagLimit ||
      isLoading ||
      isLocked ||
      isDayLocked;
    const isAddPlannedTimeDisbled = isLoading || isLocked || isDayLocked;
    const isTimerRunning = draft?.timer_state === 'start';
    const isTimerDisabled = isSaveDisabled || (!isTimerRunning && !draft?.project.base.active);
    const isTimerHidden = isOnReportsRoute || isActingAsOtherUser;
    const {
      isEditingExisting: isEditingExistingHour,
      isCreatingNew,
      isProjectAvailable,
    } = hourDraftStatus;
    const isUserAdmin = user.admin;
    // Consider the "Planning Beta" (legacy) feature enabled only if Tasks are NOT enabled.
    const isPlanningFeatureEnabled = !!account.features.planning && !account.features.tasks;
    const isDraftUserDeleted = deletedUsers.find(user => user.id === draft?.user.id);
    // temporary until we figure out https://app.clubhouse.io/timelyapp/story/27241/not-possible-to-copy-a-time-entry-to-another-project-from-the-hour-editor-on-reports-if-the-time-entry-was-logged-by-a-deleted

    // Assume project is available when acting as another user while their project permissions
    // are loading to avoid flickering

    const shouldFetchProjectDetails =
      isProjectAvailable && !!draft?.project && !!draft.project.base.id;
    const canToggleSidebar = isRouteCalendar;
    const isCopyDisabled =
      !!isDraftUserDeleted ||
      !hasValidDraft ||
      isMissingRequiredNote ||
      isMissingRequiredTags ||
      isMissingRequiredAnyTag ||
      exceedingSingleTagLimit ||
      isLoading;
    const isMoveDisabled = isSaveDisabled || isBilled;
    const updatedAt = draft?.updated_at
      ? DateTime.fromSeconds(draft?.updated_at).toISODate()
      : null;

    const hasHourHistory =
      updatedAt != null &&
      isAfter(updatedAt, config.auditsLaunched) &&
      !!draft?.id &&
      !!account.features.audits;

    const linkedForecast =
      draft?.forecast_id && forecasts[draft?.forecast_id] ? forecasts[draft?.forecast_id] : null;

    const hasPermission = draft?.state?.permissions.some(p => p.role_id === user.role_id);
    const allMemoriesInTimestamps = draft?.timestamps?.reduce(
      (acc, timestamp) => {
        acc.push(...timestamp.entry_ids);
        return acc;
      },
      [] as Array<string | number>
    );
    const hasMemoriesInTimestamps = allMemoriesInTimestamps
      ? allMemoriesInTimestamps.length > 0
      : false;

    return {
      isCreatingNew,
      canSetTimespan,
      canToggleSidebar,
      hasPlannedTime,
      hasValidDraft,
      hasForecast: !!draft?.forecast_id,
      hasHourHistory,
      hasMemories: !!draft?.entry_ids.length,
      hasMemoriesInTimestamps,
      isActingAsOtherUser,
      isBasedOnSuggestion,
      canManage,
      isBilled,
      isInvoiced,
      isCopyDisabled,
      isAddPlannedTimeDisbled,
      isMoveDisabled,
      isEditingExistingHour,
      isLinkedForecastCompleted: !!linkedForecast?.completed_at,
      isLocked,
      isDayLocked,
      isMissingRequiredNote,
      isPlanningFeatureEnabled,
      isProjectAvailable,
      isSaveDisabled,
      isTimerDisabled,
      isTimerHidden,
      isLoading,
      isTimerRunning,
      isUserAdmin,
      shouldFetchProjectDetails,
      isMaybeChangedDueToPusherUpdate: !!draft?._pusherUpdatedAt,
      canUserUnlock: isUserAdmin || hasPermission,
      canUserCreateProject: isUserAdmin,
      canUserManageTags: isUserAdmin,
      showTimestamp: !!draft && draft.timestamps?.length > 0,
      showMemoryDrawer: !memoryViewMode.tic || memoryViewMode.viewmode === 'timeline',
    };
  }
);

export type HourDraftMeta = ReturnType<typeof getDraftMeta>;

const getAccountAndUser = createSelector([getCurrentAccount, getUser], (account, user) => ({
  account,
  user,
}));

const getAutoNoteStrategy = createSelector(
  [readAutoNoteStrategy, getPreferredAutoNoteStrategy],
  (currentAutoNoteStrategy, defaultAutoNoteStrategy) => ({
    defaultAutoNoteStrategy,
    currentAutoNoteStrategy,
  })
);

const getHourEditorExperiments = createSelector(
  [getIsUserInExperimentalFeatureGroup, getHasEnabledTic],
  (showDebugInfo, hasEnabledTic) => ({
    showDebugInfo,
    hasEnabledTic,
  })
);

const getDefaultTagIdsForHourDraft = createSelector(
  [getHourDraftProject],
  hourDraftProject => {
    return hourDraftProject?.fullProject?.default_label_ids ?? EMPTY_ARRAY;
  },
  { memoizeOptions: { resultEqualityCheck: isEqual } }
);

const getHourEditorClientFocusOnProjects = createSelector(
  [
    getCanUseClientFocusOnProjects,
    getUserProperties,
    getHourDraftShowAllProjectsForDraft,
    getHourDraftExternalLinks,
    getTicTasks,
    getClients,
  ],
  (
    canUseClientFocusOnProjects,
    userProperties,
    showAllProjectsForDraft,
    externalLinks,
    tasks,
    clients
  ) =>
    resolveClientFocusOnProjects({
      canUseClientFocusOnProjects,
      shouldUseClientFocusOnProjects: userProperties[CLIENT_FOCUS_ON_PROJECTS_BY_DEFAULT] !== false,
      showAllProjectsForDraft,
      externalLinks,
      tasks,
      clients,
    })
);

export const getHourEditorUI = createSelector(
  [
    getAccountAndUser,
    getAutoNoteStrategy,
    getUserProperties,
    getDraftMeta,
    getHourEditorMemoryPreferences,
    getAllowedTagsForHourDraft,
    isHourDraftMissingRequiredTags,
    getHourDraftProjectId,
    getHourEditorMessages,
    getAccountFeatures,
    getHourEditorExperiments,
    getHourDraftTagIds,
    getDefaultTagIdsForHourDraft,
    getTimestampSnappingMinutes,
    getHasMemoriesWithinHourDraftTimespan,
    getHourEditorClientFocusOnProjects,
  ],
  (
    { account, user },
    { currentAutoNoteStrategy, defaultAutoNoteStrategy },
    userProperties,
    draftMeta,
    memoryPreferences,
    tags,
    isMissingRequiredTags,
    projectId,
    messages,
    features,
    { showDebugInfo, hasEnabledTic },
    selectedTags,
    defaultTagIds,
    snapToMinutes,
    timelineHasMemoriesWithinTimestampRanges,
    clientFocusOnProjects
  ) => {
    const userHasMemories = !!window.BOOTSTRAPPED_DATA.user.last_received_memories_date;
    const hasInstalledMemory = userHasMemories || !!userProperties[HAS_INSTALLED_MEMORY_TRACKER];
    const showMemorySection =
      (draftMeta.canToggleSidebar && hasInstalledMemory) || draftMeta.hasMemories;
    const hasMemoryOrLinkedMemories = hasInstalledMemory || draftMeta.hasMemories;

    const hasNonDefaultAvailableTags =
      tags.length > 0 && !tags.every(tag => defaultTagIds.includes(tag.id));
    const hasNonDefaultSelectedTags =
      selectedTags?.length > 0 && !selectedTags.every(id => defaultTagIds.includes(id));

    return {
      timeFormat: user.time_format,
      snapToMinutes,
      showMessages: messages.length > 0,
      messages,
      accountId: account.id,
      startOfWeek: account.start_of_week,
      hasSelectedProject: !!projectId,
      isBasedOnMention: false,
      isMissingRequiredTags,
      showMemories: memoryPreferences.isVisible,
      isProjectEditEnabled: !(draftMeta.isPlanningFeatureEnabled && draftMeta.hasPlannedTime),
      isAuditFeatureEnabled: !!features.audits,
      isDayLockingFeatureEnabled: !!features.day_locking,
      isHourStatesFeatureEnabled: !!features.hour_states,
      isBillingFeatureEnabled: !!features.billing,
      showTags: hasNonDefaultAvailableTags || hasNonDefaultSelectedTags,
      saveButtonLabel: draftMeta.isBasedOnSuggestion ? 'Accept' : 'Save',
      showForecastCompleteButton:
        draftMeta.isCreatingNew && draftMeta.hasForecast && !draftMeta.isLinkedForecastCompleted,
      hasInstalledMemory,
      hasMemoryOrLinkedMemories,
      showMemorySection,
      showDebugInfo,
      hasEnabledTic,
      timelineHasMemoriesWithinTimestampRanges,
      clientFocusOnProjects,
      dontShowBillableChangedWarningAgain:
        !!userProperties[DONT_SHOW_BILLABLE_CHANGED_WARNING_AGAIN],
      autoNoteStrategy: currentAutoNoteStrategy,
      defaultAutoNoteStrategy,
      userRoleId: user.role_id,
      ...draftMeta,
    };
  }
);

export const getHourEditorProps = createSelector(
  [
    getUser,
    getEnrichedHourDraft,
    getHourEntities,
    getAllowedTagsForHourDraft,
    getAllowedTagsMap,
    getRequiredTagIdsForHourDraft,
    getHourEditorDurationSuggestions,
    getHintsForHourEditor,
    getDurationOfSelectedMemories,
    getHourEditorUI,
    getDefaultTagIdsForHourDraft,
  ],
  (
    user,
    draft,
    hourEntities,
    tags,
    allTags,
    requiredTagIds,
    durationOptions,
    hints,
    selectedMemoriesDuration,
    ui,
    defaultTagIds
  ) => {
    const relatedMemories = draft?.entry_ids || [];
    const { suggestedDurations, durationForMode } = durationOptions;

    return {
      draft: draft!,
      hourDraftIsBasedOn: draft?.id ? hourEntities?.[draft?.id] : undefined,
      durationForMode,
      suggestion: null,
      tags,
      allTags,
      relatedMemories,
      suggestedDurations,
      selectedMemoriesDuration,
      userTimeZone: user.time_zone,
      requiredTagIds,
      hints,
      ui,
      defaultTagIds,
    };
  }
);

export type HourEditorProps = ReturnType<typeof getHourEditorProps>;

export interface GetHourDraftTimespans {
  hourDraftExists: boolean;
  adjustableRanges: Array<Range>;
  hourDraftId?: number;
}

export const getWeeklyCapacityProps = createSelector(
  [
    getLoggedWeeklyTotal,
    getHourDraft,
    getUserCapacitiesEntities,
    getHourEntities,
    getHourEditorDurationSuggestions,
  ],
  (loggedWeeklyTotal, hourDraft, userCapacities, hourEntities, durationOptions) => {
    const defaults = {
      isOverRemainingCapacity: false,
      overCapacityAmount: 0,
    };
    if (!hourDraft) {
      return defaults;
    }
    const { user, id } = hourDraft;
    const { durationForMode } = durationOptions;
    const loggedTotal = loggedWeeklyTotal || 0;
    // look up the capacity on the day that we're currently dealing with to ensure accuracy
    const userCapacityHistory = userCapacities != null ? userCapacities[user.id] : null;
    const currentCapacity = userCapacityHistory
      ? getHistoricCapacityByDate(userCapacityHistory, hourDraft.day)
      : null;

    // No weekly logged total or we're already over, no need to continue
    if (
      (!loggedTotal && !durationForMode.total_hours) ||
      (currentCapacity && loggedTotal > currentCapacity.weekly_capacity)
    ) {
      return defaults;
    }

    const existingHour = id ? hourEntities?.[id] : null;
    const existingHourTotalHours = existingHour?.duration.total_hours || 0;

    // Are we reducing the logged time on an existing hour? If so, no need to continue
    if (durationForMode.total_hours < existingHourTotalHours) {
      return defaults;
    }

    const draftTotalMinusLogged = durationForMode.total_hours - existingHourTotalHours;
    const draftLoggedTotal = draftTotalMinusLogged + loggedTotal;
    const isOverRemainingCapacity = currentCapacity
      ? draftLoggedTotal > currentCapacity.weekly_capacity
      : false;

    return {
      isOverRemainingCapacity,
      overCapacityAmount:
        isOverRemainingCapacity && currentCapacity
          ? +Math.abs(draftLoggedTotal - currentCapacity.weekly_capacity).toFixed(2)
          : 0,
    };
  }
);
